import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import axios from 'axios';
import SuperAdminLayout from '../../../layouts/SuperAdminLayout';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from './../../../utils/api';

const BalanceReport = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const superAdminId = user?.id;
  const [balances, setBalances] = useState([]);

  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const res = await api.get(`/api/reports/balance/${superAdminId}`);
        setBalances(res.data);
      } catch (err) {
        console.error('Error fetching balance report:', err);
      }
    };

    if (superAdminId) fetchBalances();
  }, [superAdminId]);

  // Calculate totals and closing balance logic
  const totalDebit = balances.reduce(
    (sum, entry) => sum + (entry.debit || 0),
    0
  );
  const totalCredit = balances.reduce(
    (sum, entry) => sum + (entry.credit || 0),
    0
  );

  const closingDebit = totalCredit > totalDebit ? totalCredit - totalDebit : 0;
  const closingCredit = totalDebit > totalCredit ? totalDebit - totalCredit : 0;

  const downloadExcel = () => {
    const rows = balances.map((entry) => ({
      'Party Name': entry.partyName,
      'Debit (Dr)': entry.debit > 0 ? entry.debit.toFixed(2) : '-',
      'Credit (Cr)': entry.credit > 0 ? entry.credit.toFixed(2) : '-',
    }));

    // Totals
    const totalDebit = balances.reduce(
      (sum, entry) => sum + (entry.debit || 0),
      0
    );
    const totalCredit = balances.reduce(
      (sum, entry) => sum + (entry.credit || 0),
      0
    );

    const closingDebit =
      totalCredit > totalDebit ? totalCredit - totalDebit : 0;
    const closingCredit =
      totalDebit > totalCredit ? totalDebit - totalCredit : 0;

    rows.push({
      'Party Name': 'Closing Balance',
      'Debit (Dr)': closingDebit > 0 ? closingDebit.toFixed(2) : '-',
      'Credit (Cr)': closingCredit > 0 ? closingCredit.toFixed(2) : '-',
    });

    rows.push({
      'Party Name': 'Total',
      'Debit (Dr)': (totalDebit + closingDebit).toFixed(2),
      'Credit (Cr)': (totalCredit + closingCredit).toFixed(2),
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Report');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, 'balance-report.xlsx');
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="screen" sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Balance Report
        </Typography>

        <Button variant="outlined" sx={{ mb: 2 }} onClick={downloadExcel}>
          Download Excel
        </Button>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f4f4f4' }}>
              <TableRow>
                <TableCell>Party Name</TableCell>
                <TableCell>Debit (Dr)</TableCell>
                <TableCell>Credit (Cr)</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {balances.map((entry, idx) => (
                <TableRow key={idx}>
                  <TableCell>{entry.partyName}</TableCell>
                  <TableCell>
                    {entry.debit > 0 ? entry.debit.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell>
                    {entry.credit > 0 ? entry.credit.toFixed(2) : '-'}
                  </TableCell>
                </TableRow>
              ))}

              {/* Closing Balance Row */}
              <TableRow sx={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                <TableCell>Closing Balance</TableCell>
                <TableCell>
                  {closingDebit > 0 ? closingDebit.toFixed(2) : '-'}
                </TableCell>
                <TableCell>
                  {closingCredit > 0 ? closingCredit.toFixed(2) : '-'}
                </TableCell>
              </TableRow>

              {/* Final Totals Row */}
              <TableRow sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}>
                <TableCell>Total</TableCell>
                <TableCell>{(totalDebit + closingDebit).toFixed(2)}</TableCell>
                <TableCell>
                  {(totalCredit + closingCredit).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </SuperAdminLayout>
  );
};

export default BalanceReport;
